import React, { useState, useEffect, useRef } from "react";
import { X, Mic, Flame, Droplets, Volume2, VolumeX, Anchor, ArrowRight, Home } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import useSpeechRecognition from "../../hooks/useSpeechRecognition";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";

export default function VentBox({ onClose }) {
    const { user, breakFree } = useAuth(); // NEW
    const { t } = useLanguage();
    const [mode, setMode] = useState(null); // 'fire' | 'water' | null
    const [content, setContent] = useState("");
    const [isReleasing, setIsReleasing] = useState(false);
    const [isComplete, setIsComplete] = useState(false); // To show the invitation modal
    const navigate = useNavigate();

    // Audio State
    const [isAudioEnabled, setIsAudioEnabled] = useState(() => {
        const saved = localStorage.getItem("ayi_vent_audio");
        return saved !== null ? JSON.parse(saved) : true;
    });

    const audioRef = useRef(null);

    const { isListening, transcript, volume, startListening, stopListening, resetTranscript } = useSpeechRecognition();

    // Handle Audio Mute/Unmute
    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.muted = !isAudioEnabled;
        }
        localStorage.setItem("ayi_vent_audio", JSON.stringify(isAudioEnabled));
    }, [isAudioEnabled]);

    // Update audio source when mode changes
    useEffect(() => {
        if (audioRef.current && mode) {
            audioRef.current.src = mode === 'fire' ? '/The_Holy_Pyre_2.0.mp3' : '/The open sea 2.0.mp3';
            // Start volume at 1
            audioRef.current.volume = 1;
            audioRef.current.play().catch(e => console.log("Audio autoplay prevented", e));
        }
    }, [mode]);

    // Commit transcript on stop
    useEffect(() => {
        if (!isListening && transcript) {
            setContent(prev => (prev + " " + transcript).trim());
            resetTranscript();
        }
    }, [isListening, transcript, resetTranscript]);

    const toggleListening = () => {
        if (isListening) stopListening();
        else startListening();
    };

    const handleRelease = () => {
        if (!content.trim()) return onClose();

        setIsReleasing(true);
        if (audioRef.current) {
            // Fade out audio over 4 seconds (40 steps of 100ms)
            let step = 0;
            let fadeOut = setInterval(() => {
                step++;
                if (audioRef.current.volume > 0.02) {
                    audioRef.current.volume -= 0.025;
                }
                if (step >= 40) {
                    audioRef.current.volume = 0;
                    clearInterval(fadeOut);
                }
            }, 100);
        }

        // After 5s animation, show completion modal
        setTimeout(() => {
            setIsComplete(true);
        }, 5000);
    };

    const displayValue = isListening ? `${content} ${transcript}` : content;
    const micScale = 1 + (volume / 200);

    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4" style={{
            backgroundColor: mode === 'water' ? 'rgba(5, 15, 25, 0.9)' : (mode === 'fire' ? 'rgba(15, 5, 5, 0.95)' : 'rgba(10, 10, 10, 0.8)'),
            backdropFilter: 'blur(12px)',
            transition: 'background-color 1.5s ease'
        }}>
            {/* Audio Element */}
            <audio ref={audioRef} loop preload="auto" />

            <AnimatePresence mode="wait">
                {!mode ? (
                    /* The Threshold (Choice) */
                    <motion.div
                        key="threshold"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        className="w-full max-w-4xl flex flex-col items-center justify-center relative"
                    >
                        <button onClick={onClose} className="absolute -top-12 right-0 md:right-4 text-gray-500 hover:text-white bg-black/50 p-2 rounded-full backdrop-blur-md transition-colors z-50">
                            <X className="w-6 h-6" />
                        </button>

                        <h2 className="text-2xl md:text-3xl font-display text-white mb-8 md:mb-12 tracking-widest uppercase font-light text-center">{t('vent_title')}</h2>

                        <div className="flex flex-col md:flex-row gap-6 w-full px-4">
                            {/* FIRE CHOICE */}
                            <button
                                onClick={() => setMode('fire')}
                                className="flex-1 group relative overflow-hidden rounded-2xl border border-orange-900/30 bg-black/60 hover:bg-orange-950/40 p-8 h-64 md:h-80 transition-all duration-500 flex flex-col items-center justify-center gap-4 md:gap-6 shadow-2xl"
                            >
                                <div className="absolute inset-0 bg-gradient-to-t from-orange-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                <Flame className="w-12 h-12 md:w-16 md:h-16 text-orange-500/50 group-hover:text-orange-500 transition-colors duration-500 group-hover:scale-110" />
                                <div className="text-center relative z-10">
                                    <h3 className="text-xl md:text-2xl font-serif text-gray-200 mb-2">{t('vent_fire_title')}</h3>
                                    <p className="text-gray-400 text-xs md:text-sm italic font-serif group-hover:text-orange-200/70 transition-colors px-4">"{t('vent_fire_desc')}"</p>
                                </div>
                            </button>

                            {/* WATER CHOICE */}
                            <button
                                onClick={() => setMode('water')}
                                className="flex-1 group relative overflow-hidden rounded-2xl border border-blue-900/30 bg-black/60 hover:bg-slate-900/60 p-8 h-64 md:h-80 transition-all duration-500 flex flex-col items-center justify-center gap-4 md:gap-6 shadow-2xl"
                            >
                                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                <Droplets className="w-12 h-12 md:w-16 md:h-16 text-blue-500/50 group-hover:text-blue-400 transition-colors duration-500 group-hover:scale-110" />
                                <div className="text-center relative z-10">
                                    <h3 className="text-xl md:text-2xl font-serif text-gray-200 mb-2">{t('vent_water_title')}</h3>
                                    <p className="text-gray-400 text-xs md:text-sm italic font-serif group-hover:text-blue-200/70 transition-colors px-4">"{t('vent_water_desc')}"</p>
                                </div>
                            </button>
                        </div>
                    </motion.div>
                ) : !isReleasing && !isComplete ? (
                    /* The Canvas (Writing Box) */
                    <motion.div
                        key="canvas"
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={
                            mode === 'water'
                                ? { y: 600, scale: 0.3, opacity: 0, filter: "blur(20px)", transition: { duration: 2.5, ease: "easeIn" } }
                                : { y: -100, opacity: 0, transition: { duration: 1.5, ease: "easeIn" } } // Fire fades/curls differently
                        }
                        className={`w-full max-w-lg rounded-2xl p-6 md:p-8 relative overflow-hidden transition-all duration-1000 ${mode === 'water'
                                ? 'bg-riverstone border-[1px] border-blue-900/40 shadow-[0_20px_50px_rgba(0,0,0,0.5)]'
                                : 'bg-bhojpatra border-[1px] border-orange-900/20 shadow-[0_0_80px_rgba(200,50,0,0.1)]'
                            }`}
                        style={{
                            boxShadow: mode === 'water' ? "inset 0 4px 10px rgba(255,255,255,0.05), inset 0 -10px 40px rgba(0,0,0,0.6), 0 20px 50px rgba(0,0,0,0.8)" : "inset 0 0 50px rgba(139, 69, 19, 0.4), 0 0 80px rgba(200,50,0,0.1)"
                        }}
                    >
                        {/* Headers */}
                        <div className="flex justify-between items-start mb-6 md:mb-8">
                            <div className="text-center w-full">
                                <h2 className={`text-2xl md:text-3xl font-display font-medium tracking-wide mb-2 ${mode === 'water' ? 'text-gray-300' : 'text-orange-950'}`}>
                                    {mode === 'water' ? t('vent_canvas_water_title') : t('vent_fire_title')}
                                </h2>
                                <p className={`text-xs md:text-sm italic font-serif ${mode === 'water' ? 'text-blue-300/60' : 'text-orange-900/60'}`}>
                                    {mode === 'water' ? t('vent_canvas_water_desc') : t('vent_canvas_fire_desc')}
                                </p>
                            </div>

                            <div className="absolute top-4 right-4 flex gap-1 md:gap-2">
                                <button onClick={() => setIsAudioEnabled(!isAudioEnabled)} className={`p-2 rounded-full ${mode === 'water' ? 'text-blue-400 hover:bg-blue-900/20' : 'text-orange-800 hover:bg-orange-900/10'}`} title={isAudioEnabled ? t('vent_mute') : t('vent_unmute')}>
                                    {isAudioEnabled ? <Volume2 className="w-5 h-5 md:w-6 md:h-6" /> : <VolumeX className="w-5 h-5 md:w-6 md:h-6" />}
                                </button>
                                <button onClick={onClose} className={`p-2 rounded-full ${mode === 'water' ? 'text-gray-500 hover:text-gray-300 hover:bg-white/5' : 'text-orange-900 hover:text-orange-950 hover:bg-black/5'}`}>
                                    <X className="w-5 h-5 md:w-6 md:h-6" />
                                </button>
                            </div>
                        </div>

                        {/* Input Area */}
                        <textarea
                            value={displayValue}
                            onChange={(e) => setContent(e.target.value)}
                            placeholder="I feel heavy because..."
                            className={`w-full h-40 md:h-48 rounded-none px-4 py-6 md:p-6 resize-none focus:outline-none transition-colors mb-6 font-serif text-base md:text-lg leading-relaxed bg-transparent border-t border-b ${mode === 'water'
                                    ? 'text-blue-100 placeholder:text-blue-900/30 border-blue-900/30 text-engraved'
                                    : 'text-orange-950 placeholder:text-orange-900/40 border-orange-900/20'
                                }`}
                            autoFocus
                        />

                        {/* Controls */}
                        <div className="flex justify-between items-center">
                            <button
                                onClick={toggleListening}
                                style={{
                                    transform: isListening ? `scale(${micScale})` : 'scale(1)',
                                }}
                                className={`flex items-center gap-2 px-3 py-2 md:px-4 md:py-2 rounded-full transition-all duration-75 ${isListening
                                        ? (mode === 'water' ? "text-blue-300 bg-blue-900/30 border border-blue-500/30 shadow-[0_0_15px_rgba(59,130,246,0.5)]" : "text-orange-800 bg-orange-900/20 border border-orange-800/30 shadow-[0_0_15px_rgba(154,52,18,0.3)]")
                                        : (mode === 'water' ? "text-blue-400/50 hover:text-blue-300 hover:bg-white/5" : "text-orange-900/60 hover:text-orange-950 hover:bg-black/5")
                                    }`}
                            >
                                {isListening ? (
                                    <>
                                        <span className={`w-2 h-2 rounded-sm animate-pulse ${mode === 'water' ? 'bg-blue-400' : 'bg-orange-800'}`} />
                                        <span className="text-xs md:text-sm font-medium">Stop</span>
                                    </>
                                ) : (
                                    <Mic className="w-5 h-5 md:w-6 md:h-6" />
                                )}
                            </button>

                            <button
                                onClick={handleRelease}
                                disabled={!content.trim()}
                                className={`flex items-center gap-2 px-5 py-2 md:px-6 md:py-3 rounded-xl font-medium shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105 ${mode === 'water'
                                        ? "bg-gradient-to-br from-blue-600 to-indigo-900 text-blue-50 hover:shadow-blue-500/40 border border-blue-500/30"
                                        : "bg-gradient-to-r from-orange-800 to-red-900 text-orange-50 hover:shadow-orange-900/40 border border-orange-900/50"
                                    }`}
                            >
                                {mode === 'water' ? <Anchor className="w-4 h-4" /> : <Flame className="w-4 h-4" />}
                                <span className="text-sm md:text-base">{mode === 'water' ? t('vent_release_water') : t('vent_release_fire')}</span>
                            </button>
                        </div>
                    </motion.div>
                ) : isReleasing && !isComplete ? (
                    /* The Release Animation */
                    <motion.div
                        key="release-animation"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex flex-col items-center justify-center text-center w-full h-screen absolute inset-0 pointer-events-none z-10"
                    >
                        {mode === 'water' ? (
                            <>
                                {/* Rising Bubbles */}
                                {[...Array(25)].map((_, i) => (
                                    <motion.div
                                        key={`bubble-${i}`}
                                        initial={{ opacity: 0, y: 100, x: (Math.random() - 0.5) * window.innerWidth * 0.8, scale: Math.random() * 0.5 + 0.2 }}
                                        animate={{ opacity: [0, 0.6, 0], y: -800, scale: Math.random() * 2 + 0.5 }}
                                        transition={{ duration: Math.random() * 2 + 2, ease: "easeIn", delay: Math.random() * 1.5 }}
                                        className="absolute w-8 h-8 rounded-full border-[1px] border-blue-200/40 bg-blue-100/10 blur-[1px]"
                                    />
                                ))}
                                <motion.h3
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: [0, 1, 1, 0], y: [20, 0, 0, -20] }}
                                    transition={{ duration: 4.5, times: [0, 0.2, 0.8, 1] }}
                                    className="text-xl md:text-2xl font-serif text-blue-200/80 italic tracking-wide px-6 leading-relaxed"
                                >
                                    {t('vent_anim_water')}
                                </motion.h3>
                            </>
                        ) : (
                            <>
                                {/* Golden Sparks */}
                                {[...Array(40)].map((_, i) => (
                                    <motion.div
                                        key={`spark-${i}`}
                                        initial={{ opacity: 0, y: 50, x: (Math.random() - 0.5) * 300, scale: Math.random() * 0.5 + 0.5 }}
                                        animate={{ opacity: [0, 1, 0], y: -600 - Math.random() * 300, x: (Math.random() - 0.5) * 600 }}
                                        transition={{ duration: Math.random() * 2.5 + 2, ease: "easeOut", delay: Math.random() * 0.8 }}
                                        className="absolute w-2 h-2 md:w-3 md:h-3 rounded-full bg-yellow-400 blur-[2px] shadow-[0_0_15px_rgba(255,215,0,1)]"
                                    />
                                ))}
                                {/* Ambient Glow */}
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.5 }}
                                    animate={{ opacity: [0, 0.4, 0], scale: 2.5 }}
                                    transition={{ duration: 4.5, ease: "easeOut" }}
                                    className="absolute w-96 h-96 bg-orange-600/40 rounded-full blur-[120px]"
                                />
                                <motion.h3
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: [0, 1, 1, 0], y: [20, 0, 0, -20] }}
                                    transition={{ duration: 4.5, times: [0, 0.2, 0.8, 1] }}
                                    className="text-xl md:text-2xl font-serif text-orange-200/80 italic tracking-wide relative z-10 px-6 leading-relaxed"
                                >
                                    {t('vent_anim_fire')}
                                </motion.h3>
                            </>
                        )}
                    </motion.div>
                ) : (
                    /* The Experiential Onboarding Modal (isComplete is true) */
                    <motion.div
                        key="invitation"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1, ease: "easeOut" }}
                        className="relative z-20 w-full max-w-md p-8 md:p-10 rounded-3xl bg-white/10 backdrop-blur-xl border border-white/20 shadow-[0_0_50px_rgba(255,255,255,0.05)] text-center flex flex-col items-center justify-center space-y-8"
                    >
                        <div className="space-y-4">
                            <h2 className="text-2xl md:text-3xl font-display text-white tracking-wide">
                                {user ? t('vent_complete_title') : t('vent_complete_title')}
                            </h2>
                            <p className="text-sm md:text-base text-gray-300 font-serif italic max-w-sm mx-auto">
                                {user
                                    ? t('vent_complete_desc')
                                    : t('vent_complete_desc_new')}
                            </p>
                        </div>

                        <div className="flex flex-col w-full space-y-4 pt-4">
                            {user && !user.isGuest ? (
                                <button
                                    onClick={onClose}
                                    className="w-full flex items-center justify-center gap-3 px-8 py-4 bg-white text-gray-900 rounded-full font-medium text-lg hover:bg-gray-100 hover:scale-105 transition-all shadow-lg"
                                >
                                    {t('vent_complete_cta')} <Home className="w-5 h-5" />
                                </button>
                            ) : (
                                <button
                                    onClick={breakFree}
                                    className="w-full flex items-center justify-center gap-3 px-8 py-4 bg-white text-gray-900 rounded-full font-medium text-lg hover:bg-gray-100 hover:scale-105 transition-all shadow-lg"
                                >
                                    {user?.isGuest ? "Yes, I want to break free" : t('vent_complete_cta_new')} <ArrowRight className="w-5 h-5" />
                                </button>
                            )}
                            {(!user || user.isGuest) && (
                                <button
                                    onClick={onClose}
                                    className="text-gray-400 font-sm hover:text-white transition-colors py-2"
                                >
                                    {t('vent_complete_cancel')}
                                </button>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
